import type { AckPlan } from './ack-plan'
import { planAcknowledgement } from './ack-plan'
import { clampLineQty, normalizeQtyDraft, parseQtyDraft } from './ack-qty'
import type { Order, ValidationIssue, ValidationRun } from '@/lib/vision/schemas'
import { VisionApiError } from '@/lib/vision/client'

export type BulkAckLineResult = 'Accept' | 'Partial' | 'Zero' | 'Rejected (validation)'

export type BulkAckQtyDraftByKey = Record<string, string>

export interface BulkAckLoadedPo {
  order: Order
  validation: ValidationRun | null
}

export interface BulkAckLineView {
  key: string
  orderId: string
  externalOrderNumber: string
  shipTo: string
  itemSequenceNumber: number
  channelSku: string
  title: string
  orderedQty: number
  draft: string
  locked: boolean
  result: BulkAckLineResult
  invalid: boolean
  rejectionReasons: readonly string[]
}

export function lineKey(orderId: string, itemSequenceNumber: number): string {
  return `${orderId}:${itemSequenceNumber}`
}

export function defaultQtyDrafts(loaded: readonly BulkAckLoadedPo[]): BulkAckQtyDraftByKey {
  const next: BulkAckQtyDraftByKey = {}
  for (const { order, validation } of loaded) {
    const plan = planAcknowledgement(order, validation?.issues ?? [], {})
    const rejected = new Set(plan.rejected.map(line => line.itemSequenceNumber))
    for (const item of order.items) {
      const key = lineKey(order.id, item.itemSequenceNumber)
      if (rejected.has(item.itemSequenceNumber)) {
        next[key] = ''
        continue
      }
      const ordered = Number(item.orderedQuantity ?? 0)
      next[key] = ordered === 0 ? '0' : String(ordered)
    }
  }
  return next
}

/** Strict draft parse for the bulk grid — empty / non-integer / OOB stays invalid. */
export function parseBulkAckDraft(
  draft: string,
  orderedQty: number,
): { qty: number | null; invalid: boolean } {
  const normalized = normalizeQtyDraft(draft)
  if (normalized !== draft.trim() && draft.trim() !== '') {
    // Raw had non-digits or a minus — normalizeQtyDraft already stripped them.
  }
  if (draft.trim() === '') return { qty: null, invalid: true }
  if (/[^\d]/.test(draft.trim())) return { qty: null, invalid: true }
  const parsed = parseQtyDraft(normalized)
  if (parsed == null) return { qty: null, invalid: true }
  if (parsed < 0 || parsed > orderedQty) return { qty: parsed, invalid: true }
  return { qty: parsed, invalid: false }
}

export function qtyBySeqForOrder(
  order: Order,
  drafts: BulkAckQtyDraftByKey,
): Record<number, number> {
  const qtyBySeq: Record<number, number> = {}
  for (const item of order.items) {
    const key = lineKey(order.id, item.itemSequenceNumber)
    const ordered = Number(item.orderedQuantity ?? 0)
    const { qty, invalid } = parseBulkAckDraft(drafts[key] ?? '', ordered)
    if (!invalid && qty != null) qtyBySeq[item.itemSequenceNumber] = qty
    else qtyBySeq[item.itemSequenceNumber] = clampLineQty(qty ?? ordered, ordered)
  }
  return qtyBySeq
}

export function planForLoadedPo(loaded: BulkAckLoadedPo, drafts: BulkAckQtyDraftByKey): AckPlan {
  return planAcknowledgement(
    loaded.order,
    loaded.validation?.issues ?? [],
    qtyBySeqForOrder(loaded.order, drafts),
  )
}

export function lineResultLabel(
  orderedQty: number,
  acceptedQty: number | null,
  locked: boolean,
): BulkAckLineResult {
  if (locked) return 'Rejected (validation)'
  if (acceptedQty == null) return 'Accept'
  if (acceptedQty === 0) return 'Zero'
  if (acceptedQty < orderedQty) return 'Partial'
  return 'Accept'
}

export function buildBulkAckLines(
  loaded: readonly BulkAckLoadedPo[],
  drafts: BulkAckQtyDraftByKey,
): BulkAckLineView[] {
  const rows: BulkAckLineView[] = []
  for (const entry of loaded) {
    const plan = planForLoadedPo(entry, drafts)
    const rejected = new Map(
      plan.rejected.map(line => [line.itemSequenceNumber, line] as const),
    )
    for (const item of entry.order.items) {
      const key = lineKey(entry.order.id, item.itemSequenceNumber)
      const orderedQty = Number(item.orderedQuantity ?? 0)
      const locked = rejected.has(item.itemSequenceNumber)
      const draft = drafts[key] ?? (locked ? '' : orderedQty === 0 ? '0' : String(orderedQty))
      const parsed = locked ? { qty: null, invalid: false } : parseBulkAckDraft(draft, orderedQty)
      const rejectedLine = rejected.get(item.itemSequenceNumber)
      rows.push({
        key,
        orderId: entry.order.id,
        externalOrderNumber: entry.order.externalOrderNumber,
        shipTo: entry.order.shipToLocationCode ?? '—',
        itemSequenceNumber: item.itemSequenceNumber,
        channelSku: item.channelSku,
        title: item.displayName || item.channelSku,
        orderedQty,
        draft,
        locked,
        result: lineResultLabel(orderedQty, parsed.qty, locked),
        invalid: parsed.invalid,
        rejectionReasons:
          rejectedLine?.checks.map(check =>
            [check.reason ?? check.family, check.detail].filter(Boolean).join(' — '),
          ) ?? [],
      })
    }
  }
  return rows
}

export interface BulkAckSummary {
  poCount: number
  lineCount: number
  acceptedUnits: number
  zeroQtyLines: number
  validationRejectedLines: number
  hasInvalid: boolean
}

export function summarizeBulkAck(
  loaded: readonly BulkAckLoadedPo[],
  drafts: BulkAckQtyDraftByKey,
): BulkAckSummary {
  const lines = buildBulkAckLines(loaded, drafts)
  let acceptedUnits = 0
  let zeroQtyLines = 0
  let validationRejectedLines = 0
  let hasInvalid = false
  for (const line of lines) {
    if (line.locked) {
      validationRejectedLines += 1
      continue
    }
    if (line.invalid) {
      hasInvalid = true
      continue
    }
    const qty = parseBulkAckDraft(line.draft, line.orderedQty).qty ?? 0
    acceptedUnits += qty
    if (qty === 0) zeroQtyLines += 1
  }
  return {
    poCount: loaded.length,
    lineCount: lines.length,
    acceptedUnits,
    zeroQtyLines,
    validationRejectedLines,
    hasInvalid,
  }
}

export function setAllEditableDrafts(
  loaded: readonly BulkAckLoadedPo[],
  drafts: BulkAckQtyDraftByKey,
  value: 'ordered' | 0,
  orderId?: string,
): BulkAckQtyDraftByKey {
  const next = { ...drafts }
  for (const entry of loaded) {
    if (orderId && entry.order.id !== orderId) continue
    const plan = planAcknowledgement(entry.order, entry.validation?.issues ?? [], {})
    const rejected = new Set(plan.rejected.map(line => line.itemSequenceNumber))
    for (const item of entry.order.items) {
      if (rejected.has(item.itemSequenceNumber)) continue
      const key = lineKey(entry.order.id, item.itemSequenceNumber)
      const ordered = Number(item.orderedQuantity ?? 0)
      next[key] = value === 'ordered' ? (ordered === 0 ? '0' : String(ordered)) : '0'
    }
  }
  return next
}

export function applyPastedColumn(
  startKey: string,
  pasteText: string,
  lines: readonly BulkAckLineView[],
  drafts: BulkAckQtyDraftByKey,
): BulkAckQtyDraftByKey {
  const startIndex = lines.findIndex(line => line.key === startKey)
  if (startIndex < 0) return drafts
  const values = pasteText
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .map(cell => cell.split('\t')[0]!.trim())
    .filter((cell, index, all) => !(index === all.length - 1 && cell === ''))
  const next = { ...drafts }
  for (let offset = 0; offset < values.length; offset += 1) {
    const line = lines[startIndex + offset]
    if (!line || line.locked) continue
    next[line.key] = normalizeQtyDraft(values[offset]!)
  }
  return next
}

export function draftsDifferFromDefaults(
  loaded: readonly BulkAckLoadedPo[],
  drafts: BulkAckQtyDraftByKey,
): boolean {
  const defaults = defaultQtyDrafts(loaded)
  const keys = new Set([...Object.keys(defaults), ...Object.keys(drafts)])
  for (const key of keys) {
    if ((drafts[key] ?? '') !== (defaults[key] ?? '')) return true
  }
  return false
}

export function rejectionReasonsFromIssues(
  issues: readonly ValidationIssue[],
): string[] {
  return issues
    .filter(issue => issue.blocking || issue.severity === 'FAIL' || issue.severity === 'HOLD')
    .map(issue => [issue.code, issue.message].filter(Boolean).join(' — '))
}

export type BulkAckPoOutcomeKind =
  | 'success'
  | 'skipped'
  | 'needs_review'
  | 'retryable'
  | 'unknown'
  | 'failed'

export interface BulkAckPoOutcome {
  kind: BulkAckPoOutcomeKind
  message: string
  retryable: boolean
  code?: string
}

export function classifyAckFailure(error: unknown): BulkAckPoOutcome {
  if (!(error instanceof VisionApiError)) {
    return {
      kind: 'failed',
      message: error instanceof Error ? error.message : 'Acknowledge failed',
      retryable: false,
    }
  }
  const code = problemCode(error.problem)
  if (error.status === 409 && code === 'REJECTION_SET_CHANGED') {
    return {
      kind: 'needs_review',
      message: error.message || 'Validation changed — review this PO',
      retryable: false,
      code,
    }
  }
  if (
    error.status === 409 &&
    (code === 'AMAZON_PO_NOT_ACKNOWLEDGEABLE' ||
      /already acknowledged/i.test(error.message) ||
      /already acknowledged/i.test(problemTitle(error.problem) ?? ''))
  ) {
    return {
      kind: 'skipped',
      message: error.message || 'Already acknowledged',
      retryable: false,
      code: code ?? 'ALREADY_ACKNOWLEDGED',
    }
  }
  if (error.status === 409 && code === 'CHANNEL_ACK_NOT_SUPPORTED') {
    return {
      kind: 'skipped',
      message: error.message || 'Channel does not acknowledge in Pulse',
      retryable: false,
      code,
    }
  }
  if (
    error.status === 503 ||
    code === 'AMAZON_ACK_PREFLIGHT_UNAVAILABLE' ||
    problemRetryable(error.problem)
  ) {
    return {
      kind: 'retryable',
      message: error.message || 'Amazon preflight unavailable — try again',
      retryable: true,
      code: code ?? 'AMAZON_ACK_PREFLIGHT_UNAVAILABLE',
    }
  }
  if (error.status === 502) {
    return {
      kind: 'unknown',
      message:
        error.message ||
        'Amazon outcome unknown — check Vendor Central / Pulse before retrying',
      retryable: false,
      code,
    }
  }
  if (error.status === 404) {
    return {
      kind: 'failed',
      message: error.message || 'Order not found',
      retryable: false,
      code,
    }
  }
  return {
    kind: 'failed',
    message: error.message || 'Acknowledge failed',
    retryable: false,
    code,
  }
}

function problemCode(problem: unknown): string | undefined {
  if (!problem || typeof problem !== 'object' || !('code' in problem)) return undefined
  const code = (problem as { code?: unknown }).code
  return typeof code === 'string' ? code : undefined
}

function problemTitle(problem: unknown): string | undefined {
  if (!problem || typeof problem !== 'object' || !('title' in problem)) return undefined
  const title = (problem as { title?: unknown }).title
  return typeof title === 'string' ? title : undefined
}

function problemRetryable(problem: unknown): boolean {
  if (!problem || typeof problem !== 'object' || !('retryable' in problem)) return false
  return (problem as { retryable?: unknown }).retryable === true
}
